import React, { useEffect, useMemo, useState } from 'react';
import { Job } from '../types';
import { buildShareDraft, ShareMedium } from '../utils/shareTemplates';

interface ShareComposerModalProps {
  open: boolean;
  job: Job;
  jobUrl: string;
  linkedInOAuthUrl: string;
  onClose: () => void;
}

const MEDIUMS: Array<{ key: ShareMedium; label: string }> = [
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'email', label: 'Email' },
];

const ShareComposerModal: React.FC<ShareComposerModalProps> = ({
  open,
  job,
  jobUrl,
  linkedInOAuthUrl,
  onClose,
}) => {
  const [medium, setMedium] = useState<ShareMedium>('linkedin');
  const [drafts, setDrafts] = useState<Record<ShareMedium, string>>({
    linkedin: '',
    whatsapp: '',
    facebook: '',
    email: '',
  });
  const [emailSubject, setEmailSubject] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    const linkedIn = buildShareDraft(job, 'linkedin', jobUrl);
    const whatsapp = buildShareDraft(job, 'whatsapp', jobUrl);
    const facebook = buildShareDraft(job, 'facebook', jobUrl);
    const email = buildShareDraft(job, 'email', jobUrl);
    setDrafts({
      linkedin: linkedIn.text,
      whatsapp: whatsapp.text,
      facebook: facebook.text,
      email: email.text,
    });
    setEmailSubject(email.emailSubject || '');
    setMedium('linkedin');
    setCopied(false);
  }, [open, job, jobUrl]);

  const currentDraft = drafts[medium] || '';

  const whatsappShareUrl = useMemo(
    () => `https://wa.me/?text=${encodeURIComponent(currentDraft)}`,
    [currentDraft]
  );

  const facebookShareUrl = useMemo(
    () => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(jobUrl)}`,
    [jobUrl]
  );

  const mailtoUrl = useMemo(
    () => `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(drafts.email || '')}`,
    [emailSubject, drafts.email]
  );

  if (!open) return null;

  const handleResetTemplate = () => {
    const fresh = buildShareDraft(job, medium, jobUrl);
    setDrafts((prev) => ({ ...prev, [medium]: fresh.text }));
    if (medium === 'email') {
      setEmailSubject(fresh.emailSubject || '');
    }
    setCopied(false);
  };

  const handleCopy = async () => {
    const contentToCopy = medium === 'email'
      ? `Subject: ${emailSubject}\n\n${currentDraft}`
      : currentDraft;
    try {
      await navigator.clipboard.writeText(contentToCopy);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      setCopied(false);
    }
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1200 }} onClick={onClose}>
      <div
        className="modal"
        style={{ width: 'min(860px, 96vw)', maxWidth: '860px', padding: '20px 22px' }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal-header" style={{ marginBottom: '12px' }}>
          <h3 style={{ margin: 0 }}>Share Post Composer</h3>
          <button type="button" className="btn ghost" onClick={onClose}>
            Close
          </button>
        </div>

        <p className="muted" style={{ marginTop: 0, marginBottom: '12px' }}>
          Select a platform, edit content if needed, then copy or open the share flow.
        </p>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
          {MEDIUMS.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`btn ${medium === item.key ? '' : 'ghost'}`}
              onClick={() => {
                setMedium(item.key);
                setCopied(false);
              }}
              style={{ padding: '8px 12px', fontSize: '14px' }}
            >
              {item.label}
            </button>
          ))}
        </div>

        {medium === 'email' && (
          <div className="form-group" style={{ marginBottom: '10px' }}>
            <label className="label">Email Subject</label>
            <input
              className="input"
              value={emailSubject}
              onChange={(event) => setEmailSubject(event.target.value)}
            />
          </div>
        )}

        <div className="form-group" style={{ marginBottom: '12px' }}>
          <label className="label">Share Content</label>
          <textarea
            className="input textarea"
            value={currentDraft}
            onChange={(event) => setDrafts((prev) => ({ ...prev, [medium]: event.target.value }))}
            rows={16}
            style={{ whiteSpace: 'pre-wrap' }}
          />
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button type="button" className="btn" onClick={handleCopy}>
            {copied ? 'Copied' : 'Copy Content'}
          </button>
          <button type="button" className="btn ghost" onClick={handleResetTemplate}>
            Reset Template
          </button>

          {medium === 'linkedin' && (
            <a href={linkedInOAuthUrl} target="_blank" rel="noopener noreferrer" className="btn ghost">
              Post on LinkedIn (OAuth)
            </a>
          )}

          {medium === 'whatsapp' && (
            <a href={whatsappShareUrl} target="_blank" rel="noopener noreferrer" className="btn ghost">
              Open WhatsApp Share
            </a>
          )}

          {medium === 'facebook' && (
            <a href={facebookShareUrl} target="_blank" rel="noopener noreferrer" className="btn ghost">
              Open Facebook Share
            </a>
          )}

          {medium === 'email' && (
            <a href={mailtoUrl} className="btn ghost">
              Open Email Draft
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareComposerModal;

